import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ChecklistHeaderService } from '@/app/modules/checklist-header/core/service';
import { ChecklistHeaderFindAllResponse, HeaderTypeEnum } from '@/app/modules/checklist-header/core/type';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { forkJoin, Observable } from 'rxjs';
import { CompanyService } from '@/app/modules/company/core/service';
import { ImagesService } from '@/app/modules/images/core/service';
import { AuthService } from '@/app/modules/auth/core/service';
import { User } from '@/app/modules/user/core/type';

@Component({
  selector: 'app-checklist-list-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzPaginationModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzDropDownModule,
    NzTagModule,
    NzToolTipModule,
    NzDatePickerModule,
    NzCheckboxModule,
    NzSpinModule
  ],
  templateUrl: './checklist-list-modal.component.html',
  styleUrls: ['./checklist-list-modal.component.scss']
})
export class ChecklistListModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() cardName: string = '';
  @Input() initialFilters: { type?: string[], status?: string[] } = {};
  @Input() isMonitorMode = false;

  allChecklists: ChecklistHeaderFindAllResponse[] = [];
  filteredChecklists: ChecklistHeaderFindAllResponse[] = [];
  displayedChecklists: ChecklistHeaderFindAllResponse[] = [];

  // Filters
  idSearch = '';
  modelSearch = '';
  modelFilters: { text: string; value: any; checked: boolean }[] = [];
  visibleModelFilters: { text: string; value: any; checked: boolean }[] = [];

  chassisSearch = '';
  chassisFilters: { text: string; value: any; checked: boolean }[] = [];
  visibleChassisFilters: { text: string; value: any; checked: boolean }[] = [];

  userSearch = '';
  userFilters: { text: string; value: any; checked: boolean }[] = [];
  visibleUserFilters: { text: string; value: any; checked: boolean }[] = [];

  typeFilters: { text: string; value: any; checked: boolean }[] = [];
  visibleTypeFilters: { text: string; value: any; checked: boolean }[] = [];

  dateRange: Date[] = [];

  // PDF Export
  companyName: string = '';
  currentUser: User | null = null;
  logoBase64: string | null = null;
  logoWidth: number = 0;
  logoHeight: number = 0;

  pageIndex = 1;
  pageSize = 10;
  total = 0;
  loading = false;

  // Manual Pagination
  currentPage = 1;
  currentPageSize = 10;
  checklistHeaderTotal = 0;
  private autoPageInterval: any;

  constructor(
    private checklistService: ChecklistHeaderService,
    private loaderService: LoaderService,
    private router: Router,
    private companyService: CompanyService,
    private imagesService: ImagesService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (this.isMonitorMode) {
      this.currentPageSize = 12; // Menos itens por página para caber na TV sem scroll
      this.startAutoPaging();
    }
    this.authService.authenticatedUser.subscribe(user => {
      this.currentUser = user;
      const userAny = user as any;
      if (userAny?.company_id) {
        this.fetchCompanyInfo(userAny.company_id);
      }
      
      if (this.cardName) {
         this.loadData();
      }
    });
    this.loadLogo('assets/images/logo.png');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cardName'] || changes['initialFilters']) {
      if (this.cardName && this.currentUser) {
        this.currentPage = 1; 
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.autoPageInterval) {
      clearInterval(this.autoPageInterval);
    }
  }

  private startAutoPaging() {
    this.autoPageInterval = setInterval(() => {
      if (this.checklistHeaderTotal > this.currentPageSize) {
        const totalPages = Math.ceil(this.checklistHeaderTotal / this.currentPageSize);
        this.currentPage = (this.currentPage % totalPages) + 1;
        this.updateDisplayedData();
      }
    }, 10000); // Muda de página a cada 10 segundos
  }

  fetchCompanyInfo(companyId: number) {
    this.companyService.getById(companyId).subscribe({
      next: (company) => {
        this.companyName = company.name;
      },
      error: () => {
        this.companyName = 'Empresa';
      }
    });
  }

  loadLogo(url: string) {
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      this.logoWidth = img.width;
      this.logoHeight = img.height;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        this.logoBase64 = canvas.toDataURL('image/png');
      }
    };
  }

  loadData() {
    const userAny = this.currentUser as any;
    const companyId = userAny?.company_id;
    const requestedPageSize = 10000;

    const params: any = {
      pageIndex: 1,
      pageSize: requestedPageSize
    };

    if (companyId) {
      params.companyId = companyId;
    }

    this.loading = true;
    this.checklistService.findAll(params).subscribe({
      next: (response: any) => {
        let items = [];
        let total = 0;

        if (Array.isArray(response)) {
          items = response;
          total = items.length;
        } else if (response && response.items) {
          items = response.items;
          total = response.total;
        }

        this.allChecklists = items || [];
        const receivedCount = this.allChecklists.length;

        if (total > receivedCount && receivedCount > 0) {
          const effectivePageSize = receivedCount;
          const totalPages = Math.ceil(total / effectivePageSize);
          const requests = [];

          for (let i = 2; i <= totalPages; i++) {
            const nextParams = { ...params, pageIndex: i, pageSize: effectivePageSize };
            requests.push(this.checklistService.findAll(nextParams));
          }

          forkJoin(requests).subscribe({
            next: (responses: any[]) => {
              responses.forEach(res => {
                if (Array.isArray(res)) {
                  this.allChecklists = [...this.allChecklists, ...res];
                } else if (res && res.items) {
                  this.allChecklists = [...this.allChecklists, ...res.items];
                }
              });
              
              this.finishLoading();
            },
            error: (err) => {
              console.error('Error fetching remaining pages', err);
              this.finishLoading();
            }
          });
        } else {
          this.finishLoading();
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Error fetching checklists', err);
        this.allChecklists = [];
        this.finishLoading();
      }
    });
  }

  finishLoading() {
    this.loading = false;
    this.applyInitialFilters();
    this.generateFilterOptions();
  }

  applyInitialFilters() {
    let filtered = [...this.allChecklists];

    if (this.initialFilters?.status?.length) {
      filtered = filtered.filter(item => this.initialFilters.status?.includes(item.status));
    }

    if (this.initialFilters?.type?.length) {
      filtered = filtered.filter(item => this.initialFilters.type?.includes(item.type));
    }

    this.allChecklists = filtered;
    this.filteredChecklists = [...this.allChecklists];
    this.checklistHeaderTotal = this.filteredChecklists.length;
    this.updateDisplayedData();
  }

  generateFilterOptions() {
    const uniqueModels = new Set(this.allChecklists.map(item => item.ProductModel?.name).filter(n => n));
    this.modelFilters = Array.from(uniqueModels).map(name => ({ text: name, value: name, checked: false }));
    this.visibleModelFilters = [...this.modelFilters];

    const uniqueChassis = new Set(this.allChecklists.map(item => item.chassis).filter(c => c));
    this.chassisFilters = Array.from(uniqueChassis).map(chassis => ({ text: chassis, value: chassis, checked: false }));
    this.visibleChassisFilters = [...this.chassisFilters];

    const uniqueUsers = new Set(this.allChecklists.map(item => item.UserModel?.name).filter(n => n));
    this.userFilters = Array.from(uniqueUsers).map(name => ({ text: name, value: name, checked: false }));
    this.visibleUserFilters = [...this.userFilters];

    const uniqueTypes = new Set(this.allChecklists.map(item => item.type).filter(t => t));
    this.typeFilters = Array.from(uniqueTypes).map(type => ({ text: this.getTypeLabel(type), value: type, checked: false }));
    this.visibleTypeFilters = [...this.typeFilters];
  }

  onIdSearch(value: string) {
    this.idSearch = value;
    this.applyFilters();
  }

  onModelSearch(value: string) {
    this.modelSearch = value;
    this.visibleModelFilters = this.modelFilters.filter(item => item.text.toLowerCase().includes(value.toLowerCase()));
  }

  applyModelFilter() {
    this.applyFilters();
  }

  clearModelFilter() {
    this.modelSearch = '';
    this.modelFilters.forEach(f => f.checked = false);
    this.visibleModelFilters = [...this.modelFilters];
    this.applyFilters();
  }

  onChassisSearch(value: string) {
    this.chassisSearch = value;
    this.visibleChassisFilters = this.chassisFilters.filter(item => item.text.toLowerCase().includes(value.toLowerCase()));
  }

  applyChassisFilter() {
    this.applyFilters();
  }

  clearChassisFilter() {
    this.chassisSearch = '';
    this.chassisFilters.forEach(f => f.checked = false);
    this.visibleChassisFilters = [...this.chassisFilters];
    this.applyFilters();
  }

  onUserSearch(value: string) {
    this.userSearch = value;
    this.visibleUserFilters = this.userFilters.filter(item => item.text.toLowerCase().includes(value.toLowerCase()));
  }

  applyUserFilter() {
    this.applyFilters();
  }

  clearUserFilter() {
    this.userSearch = '';
    this.userFilters.forEach(f => f.checked = false);
    this.visibleUserFilters = [...this.userFilters];
    this.applyFilters();
  }

  applyTypeFilter() {
    this.applyFilters();
  }

  clearTypeFilter() {
    this.typeFilters.forEach(f => f.checked = false);
    this.visibleTypeFilters = [...this.typeFilters];
    this.applyFilters();
  }

  clearAllFilters() {
    this.idSearch = '';
    this.modelSearch = '';
    this.chassisSearch = '';
    this.userSearch = '';
    this.dateRange = [];

    this.modelFilters.forEach(f => f.checked = false);
    this.visibleModelFilters = [...this.modelFilters];

    this.chassisFilters.forEach(f => f.checked = false);
    this.visibleChassisFilters = [...this.chassisFilters];

    this.userFilters.forEach(f => f.checked = false);
    this.visibleUserFilters = [...this.userFilters];

    this.typeFilters.forEach(f => f.checked = false);
    this.visibleTypeFilters = [...this.typeFilters];

    this.applyFilters();
  }

  onDateSearch(result: Date[]) {
    this.dateRange = result;
    this.applyFilters();
  }

  applyFilters() {
    let data = [...this.allChecklists];

    if (this.idSearch) {
      data = data.filter(item => item.id && item.id.toString().includes(this.idSearch));
    }

    const selectedModels = this.modelFilters.filter(f => f.checked).map(f => f.value);
    if (selectedModels.length > 0) {
      data = data.filter(item => item.ProductModel?.name && selectedModels.includes(item.ProductModel.name));
    }

    const selectedChassis = this.chassisFilters.filter(f => f.checked).map(f => f.value);
    if (selectedChassis.length > 0) {
      data = data.filter(item => item.chassis && selectedChassis.includes(item.chassis));
    }

    const selectedUsers = this.userFilters.filter(f => f.checked).map(f => f.value);
    if (selectedUsers.length > 0) {
      data = data.filter(item => item.UserModel?.name && selectedUsers.includes(item.UserModel.name));
    }

    const selectedTypes = this.typeFilters.filter(f => f.checked).map(f => f.value);
    if (selectedTypes.length > 0) {
      data = data.filter(item => item.type && selectedTypes.includes(item.type));
    }

    if (this.dateRange && this.dateRange.length === 2) {
      const startDate = new Date(this.dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(this.dateRange[1]);
      endDate.setHours(23, 59, 59, 999);

      data = data.filter(item => {
        if (!item.date) return false;
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    this.filteredChecklists = data;
    this.checklistHeaderTotal = data.length;
    this.currentPage = 1;
    this.updateDisplayedData();
  }

  updateDisplayedData() {
    const startIndex = (this.currentPage - 1) * this.currentPageSize;
    const endIndex = startIndex + this.currentPageSize;
    this.displayedChecklists = this.filteredChecklists.slice(startIndex, endIndex);
  }

  onPageChange(pageIndex: number) {
    this.currentPage = pageIndex;
    this.updateDisplayedData();
  }

  onPageSizeChange(pageSize: number) {
    this.currentPageSize = pageSize;
    this.currentPage = 1;
    this.updateDisplayedData();
  }

  navigateToDetails(id: number) {
    this.router.navigate([`/checklist-headers/${id}`]);
  }

  exportToPdf() {
    this.generatePdf(this.filteredChecklists);
  }

  generatePdf(items: ChecklistHeaderFindAllResponse[]) {
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    let currentY = 10;
    const marginX = 14;

    const maxLogoWidth = 25;
    const maxLogoHeight = 15;
    if (this.logoBase64) {
      let finalLogoWidth = maxLogoWidth;
      let finalLogoHeight = maxLogoHeight;
      if (this.logoWidth > 0 && this.logoHeight > 0) {
        const ratio = Math.min(maxLogoWidth / this.logoWidth, maxLogoHeight / this.logoHeight);
        finalLogoWidth = this.logoWidth * ratio;
        finalLogoHeight = this.logoHeight * ratio;
      }
      const logoX = pageWidth - marginX - finalLogoWidth;
      doc.addImage(this.logoBase64, 'PNG', logoX, currentY, finalLogoWidth, finalLogoHeight);
    }

    const textStartX = marginX;
    let textY = currentY + 5;
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Suprema Check-list', textStartX, textY);
    textY += 6;

    doc.setFontSize(12);
    doc.text(`Relatório : ${this.cardName}`, textStartX, textY);
    textY += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const dateStr = new Date().toLocaleString();
    doc.text(`Gerado em: ${dateStr}`, textStartX, textY);
    textY += 5;

    const userName = this.currentUser ? this.currentUser.name : 'Desconhecido';
    doc.text(`Gerado por: ${userName}`, textStartX, textY);
    textY += 10;

    const head = [['ID', 'Modelo', 'Chassis', 'Tipo', 'Data', 'Responsável', 'Status']];
    const data = items.map(item => [
      item.id,
      item.ProductModel.name,
      item.chassis || '',
      this.getTypeLabel(item.type),
      new Date(item.date).toLocaleDateString(),
      item.UserModel.name,
      this.getStatusLabel(item.status)
    ]);

    autoTable(doc, {
      startY: textY,
      head: head,
      body: data,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [192, 20, 44], textColor: 255, fontStyle: 'bold' }
    });

    doc.save(`Relatorio-${this.cardName.replace(/\s+/g, '-')}.pdf`);
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'done': return 'Finalizado';
      case 'in_progress': return 'Em Andamento';
      case 'canceled': return 'Cancelado';
      default: return status;
    }
  }

  getTypeLabel(type: string): string {
    switch (type) {
      case 'new': return 'Novo';
      case 'used': return 'Usado';
      case 'transfer_start': return 'Envio';
      case 'transfer_done': return 'Recebimento';
      case 'vehicle': return 'Veículo';
      case 'maintenance': return 'Manutenção';
      default: return type;
    }
  }
}
